const WEAPON_DESCRIPTION_ABILITIES = [
  'Protect',
  'Shield',
  'Sniper',
  'Penetrate',
  'Recover',
  'Mend wounds',
  'Redeem',
  'Fell',
];

function collectWeaponGrantedAbilities(weapons) {
  const granted = new Set();
  if (!Array.isArray(weapons)) return granted;
  for (const weapon of weapons) {
    if (!weapon || typeof weapon !== 'object') continue;
    if (Array.isArray(weapon.abilities)) {
      weapon.abilities.forEach((ability) => {
        if (typeof ability === 'string' && ability.length > 0) granted.add(ability);
      });
    }
    if (typeof weapon.description === 'string') {
      for (const ability of WEAPON_DESCRIPTION_ABILITIES) {
        if (weapon.description.includes(ability)) granted.add(ability);
      }
    }
  }
  return granted;
}

function filterOutWeaponGrantedAbilities(abilities, weaponGranted) {
  if (!Array.isArray(abilities) || weaponGranted.size === 0) return abilities;
  return abilities.filter((ability) => !weaponGranted.has(ability));
}

/**
 * Present a deployed unit as it looked before equipped weapons (stats + icons).
 * Requires server-side originalAttackRating / originalHealth (set when weapons attach).
 */
export function buildUnitWithoutEquippedWeaponsPresentation(unit) {
  if (!unit || typeof unit !== 'object') {
    throw new Error('buildUnitWithoutEquippedWeaponsPresentation: unit is required');
  }
  if (unit.originalAttackRating === undefined) {
    throw new Error(
      `buildUnitWithoutEquippedWeaponsPresentation: unit ${unit.instanceId ?? unit.id} missing originalAttackRating`
    );
  }
  if (unit.originalHealth === undefined) {
    throw new Error(
      `buildUnitWithoutEquippedWeaponsPresentation: unit ${unit.instanceId ?? unit.id} missing originalHealth`
    );
  }

  const auraAttack = unit.auraBonuses?.attack ?? 0;
  const droneAttack = unit.droneBonuses?.attack ?? 0;
  const raidAttack = unit.raidBonuses?.attack ?? 0;
  const conditionalAttack = unit.conditionalBonuses?.attack ?? 0;
  let shrinkReduction = 0;
  if (
    unit.shrunkThisTurn &&
    unit.shrinkAmount &&
    unit.attackReductionTurn != null
  ) {
    shrinkReduction = unit.shrinkAmount;
  }

  const attackRating =
    unit.originalAttackRating +
    auraAttack +
    droneAttack +
    raidAttack +
    conditionalAttack -
    shrinkReduction;

  const auraHealth = unit.auraBonuses?.health ?? 0;
  const droneHealth = unit.droneBonuses?.health ?? 0;
  const raidHealth = unit.raidBonuses?.health ?? 0;
  const conditionalHealth = unit.conditionalBonuses?.health ?? 0;
  const maxHealth =
    unit.originalHealth + auraHealth + droneHealth + raidHealth + conditionalHealth;
  const damageTaken = unit.damageTaken ?? 0;
  const health = Math.max(0, maxHealth - damageTaken);

  const weaponGranted = collectWeaponGrantedAbilities(unit.weapons);
  const abilities = filterOutWeaponGrantedAbilities(unit.abilities, weaponGranted);
  const instanceAbilities = filterOutWeaponGrantedAbilities(unit.instanceAbilities, weaponGranted);

  let hasShield = unit.hasShield;
  if (weaponGranted.has('Shield')) {
    hasShield = false;
  }

  return {
    ...unit,
    attack_rating: attackRating,
    health,
    maxHealth,
    damage: attackRating,
    weapons: [],
    equippedWeapons: [],
    abilities,
    instanceAbilities,
    hasShield,
    weaponBonuses: { attack: 0, health: 0 },
  };
}
